import type { JobFilters } from "@js/apps/common/components/filters/config";
import type { RANGE_VALUE } from "@js/apps/jobs/apps/create-job/constants";
import type { Skill } from "@js/types/admin";
import type { ErrorWithDetail } from "@js/types/errors";
import type { Attachment } from "@js/types/uploads";
import type { IsoDateOrIsoDateTime, IsoDateTime } from "@js/utils/date/types";

import type { AccountType, User } from "./auth";
import type {
  Address,
  Location,
  SavedFilter,
  WithRecaptchaCode,
} from "./common";
import type { Employer } from "./employer";
import type {
  Freelancer,
  FreelancerBase,
  FreelancerForNodeStaff,
  WorkSampleItem,
} from "./freelancer";
import type { PaginatedResult } from "./generic";
import type { PaymentMethod } from "./payments";
import type { Role } from "./roles";

export type TimezoneOption = {
  id: string;
  value: string;
  name: string;
};

export type JobDraftTimezone = {
  timezone: string;
  timezone_id: string;
};

export type Proposal = {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    job: EmployerJobMinimal;
    created: IsoDateTime;
    status: EnumType<typeof ENUMS.BidStatus>;
  }[];
};

export type JobMinimalSearchResult = {
  id: number;
  title: string;
  employer_name: string;
  payment_type?: EnumType<typeof ENUMS.JobPaymentType>;
  budget_minimum_usd?: string;
  budget_maximum_usd?: string;
  freelancer_already_invited_to_this_job?: boolean;
};

export type JobMinimalSearchParams = {
  search?: string;
  open?: boolean;
  annotate_invited_by_client_or_matcher?: boolean;
  freelancer?: number;
  ops_invitations?: boolean;
};

export type UploadedJob = Partial<
  Pick<JobDraft, "title" | "description" | "flow_entry">
>;

export type JobsState = {
  total: number;
  fetchingFreelancerOffers: boolean;
  freelancerOffers: Array<FreelancerOffer> | null;
  savedSections: string[];
  invitationHistory: InvitationHistoryItem[];
  proposalHistory: Proposal;
  fetchingInvitationHistory: boolean;
  fetchingProposalHistory: boolean;
  fetchingJobEvents: boolean;
  uploadedJobDescription: UploadedJob | null;
  jobEvents: {
    results: Array<JobEvent>;
    count: number;
  };
};

type JobEvent = {
  event: string;
  is_matcher: boolean;
  creator: string;
  created: IsoDateTime;
  job?: string;
  jobs?: { id: number; title: string }[];
  freelancer?: string;
};

export type SkillForJob = Skill & {
  matched: boolean;
  is_superpower: boolean;
};

export type EmployerOfferBid = EmployerBid & {
  job: Pick<
    Job,
    | "creator"
    | "employer"
    | "id"
    | "introduction"
    | "job_type"
    | "level"
    | "role"
    | "title"
  >;
};

export type InitialOffer = {
  anticipated_weekly_hours: number | null;
  batch_invoice_emails: string | null;
  bid: EmployerOfferBid;
  job_title: string;
  talent_has_own_equipment: boolean;
  payment_amount: string;
  payment_type: EnumType<typeof ENUMS.JobPaymentType>;
  project_duration: EnumType<typeof ENUMS.JobContractType>;
  proposed_end_date: string | null;
  proposed_start_date: string | null;
  require_paperwork: boolean;
  status: EnumType<typeof ENUMS.OfferStatus> | null;
  deposit_payment_method: PaymentMethod | null;
  msa_client_type: EnumType<typeof ENUMS.MSAClientType> | null;
};

export type SavedJobFilter = SavedFilter<Record<string, string | boolean>>;

export type JobAction = {
  action: EnumType<typeof ENUMS.JobActions>;
  state: string;
  reason: string;
};

export type JobSkill = {
  id: number;
  skill: SkillForJob;
  is_top: boolean;
};

export type IntroVideoAttachment = {
  attachment: Attachment;
  id: number;
};

export type JobTimezone = {
  timezone: string;
  timezone_utc_offset: string;
};

export type JobLocation = Location;

type BidsCount = {
  hired_bids_count: number;
  interviewing_bids_count: number;
  interviewing_and_active_bids_count: number;
  total_bids_count: number;
};

export type JobOwnersUserData = Pick<
  User,
  | "id"
  | "avatar"
  | "avatar_thumbnail"
  | "can_be_messaged"
  | "first_name"
  | "freelancer_approved"
  | "gravatar"
  | "has_avatar_set"
  | "last_name"
  | "profile_url"
  | "public_name"
  | "title"
>;

export type JobFlowEntryType = "ai" | "custom" | "upload";

export type JobEmployer = Pick<
  Employer,
  | "id"
  | "full_link"
  | "has_logo_set"
  | "logo"
  | "logo_background_color"
  | "logo_thumbnail"
  | "name"
  | "name_color"
> & {
  link: string | null;
  login_as_user_id?: number;
};

export type JobExtension = {
  employer_calendar_link?: string;
  intro_video?: IntroVideoAttachment;
};

export type Job = {
  id: number;
  title: string;
  employer: JobEmployer;
  is_open?: boolean;
  is_byot: boolean;
  is_private: boolean;
  disable_freelancer_notifications: boolean;
  user_can_manage: boolean;
  user_is_owner: boolean;
  job_actions: Array<JobAction>;
  job_status: EnumType<typeof ENUMS.JobStatus>;
  job_skills: Array<JobSkill>;
  creator: JobCreator | null;
  level: EnumType<typeof ENUMS.SkillLevel>;
  application_questions: Array<ApplicationQuestion>;
  can_bid: boolean;
  has_freelancer_bid: boolean;
  hired_bids_count: number;
  contract_type: EnumType<typeof ENUMS.JobContractType>;
  created: string;
  deadline: IsoDateOrIsoDateTime | null;
  start_date: IsoDateOrIsoDateTime | null;
  expected_hours_per_week: number | null;
  // Old jobs may not have description but they can have introduction
  introduction?: string;
  description?: string;
  requirements?: string;
  openings_left: number;
  openings_number: number;
  payment_type: EnumType<typeof ENUMS.JobPaymentType>;
  payment_amount: string;
  main_skills: Array<SkillForJob>;
  role: Role;
  other_role_name?: string;
  timezones: JobTimezone[] | null;
  timezone_overlap: EnumType<typeof ENUMS.JobTimezoneOverlapType> | null;
  locations: JobLocation[];
  token_reward: string;
  budget_minimum_usd: string;
  budget_maximum_usd: string;
  referring_user: string | null;
  op_owners: JobOwnersUserData[] | null;
  sale_owners: JobOwnersUserData[] | null;
  published_at: string | null;
  completed_at: IsoDateOrIsoDateTime | null;
  saved_job_id: number | null;
  bids_count: BidsCount;
  job_type: EnumType<typeof ENUMS.JobType>;
  experience_as_arrow?: number;
  experience_level: EnumType<typeof ENUMS.JobExperienceLevel> | null;
  invited_by_client: boolean;
  invited_by_matcher: boolean;
  interested: boolean;
  is_resume_required: boolean;
  strong_location_requirement_met?: boolean;
  strong_timezone_requirement_met?: boolean;
  locations_strongly_required: boolean;
  timezone_required: boolean;
  // match_label: EnumType<typeof ENUMS.SmartMatchingLabel> | null;
  is_ai_interview_enabled?: boolean;
  flow_entry?: JobFlowEntryType;
  ai_interview_max_duration_in_minutes?: number;
  ai_interview_maximum_attempts?: number | null;
  status_for_freelancer?: EnumType<typeof ENUMS.JobFreelancerStatus>;
  creator_user_id?: number;
  offer_id?: number | null;
  offer_accepted_at: IsoDateTime | null;
  offer_created_at: IsoDateTime | null;
  offer_updated_at: IsoDateTime | null;
  bid_created_at: IsoDateTime | null;
  extension?: JobExtension | null;
  integration_label?: string;
  resume_smart_matching_enabled: boolean;
  sub_type: JobSubType | null;
};

export type MyJobFreelancer = Pick<
  Job,
  | "creator"
  | "employer"
  | "expected_hours_per_week"
  | "id"
  | "job_type"
  | "locations"
  | "offer_accepted_at"
  | "offer_updated_at"
  | "offer_id"
  | "payment_amount"
  | "payment_type"
  | "status_for_freelancer"
  | "timezone_overlap"
  | "timezones"
  | "title"
  | "role"
>;

export type JobForCoreTeam = Job & {
  hired_at: string | null;
  on_hold_since: IsoDateOrIsoDateTime | null;
  closed_at: IsoDateOrIsoDateTime | null;
  job_fill_support_type: EnumType<typeof ENUMS.JobFillSupportType>;
};

export type EmployerOwnJob = Pick<
  Job,
  | "created"
  | "id"
  | "job_skills"
  | "job_actions"
  | "role"
  | "title"
  | "completed_at"
  | "hired_bids_count"
  | "is_byot"
> & {
  freelancers_hired_count: number;
  freelancer_hired: Freelancer | null;
  offers_status_update: Array<{
    message: string;
  }>;
  creator: JobCreator;
  status: Job["job_status"];
  total_bids_count: BidsCount["total_bids_count"];
  subscribed_job_id: number | null;
  ats_imported: boolean;
  freelancers_with_offers: Array<FreelancerBase>;
};

export type JobStatus = EnumType<typeof ENUMS.JobStatus>;

export type ManagedJob = Pick<
  Job,
  | "id"
  | "created"
  | "title"
  | "description"
  | "deadline"
  | "introduction"
  | "application_questions"
  | "budget_maximum_usd"
  | "budget_minimum_usd"
  | "can_bid"
  | "contract_type"
  | "creator"
  | "disable_freelancer_notifications"
  | "expected_hours_per_week"
  | "experience_as_arrow"
  | "experience_level"
  | "hired_bids_count"
  | "is_open"
  | "is_private"
  | "is_resume_required"
  | "job_actions"
  | "job_status"
  | "job_type"
  | "level"
  | "locations"
  | "locations_strongly_required"
  | "openings_left"
  | "openings_number"
  | "payment_type"
  | "referring_user"
  | "requirements"
  | "start_date"
  | "timezone_overlap"
  | "timezone_required"
  | "timezones"
  | "token_reward"
  | "user_can_manage"
  | "user_is_owner"
  | "is_ai_interview_enabled"
  | "flow_entry"
  | "other_role_name"
  | "ai_interview_max_duration_in_minutes"
  | "ai_interview_maximum_attempts"
  | "extension"
> & {
  can_change_role?: boolean;
  hiring_manager?: number;
  employer: number;
  job_fill_support_type?: EnumType<typeof ENUMS.JobFillSupportType>;
  original_job: number | null;
  role?: number;
  skills: number[];
  top_skills: number[];
  job_subscribers: JobSubscriber[];
  job_subscriber_invitations: JobSubscriberInvitation[];
  auto_invite_talent: boolean;
  op_owners: number[];
  sale_owners: number[];
  talent_invoice_editing_disabled?: boolean;
  skip_approval_to_job?: boolean;
  employer_skip_approval_to_job?: boolean;
  sub_type?: JobSubType;
  generating_ai_interview_data_task_in_progress: boolean;
  automatic_linkedin_posting_enabled?: boolean | null; // null indicates that the default linkedin job posting logic should be used. True -> force enable. False -> force disable
  automatic_vonq_posting_enabled?: boolean | null; // null indicates that the default linkedin job posting logic should be used. True -> force enable. False -> force disable
  client_pays_talent_directly_enabled?: boolean;
  braintrust_pays_talent_off_platform_enabled?: boolean;
  interview_criteria?: InterviewGradingCriteria[];
  interview_questions?: InterviewQuestion[];
  limitations: {
    min_openings_number: number;
    max_openings_number: number;
  };
};

export type JobListingJob = Pick<
  Job,
  | "id"
  | "main_skills"
  | "job_skills"
  | "title"
  | "created"
  | "has_freelancer_bid"
  | "expected_hours_per_week"
  | "budget_minimum_usd"
  | "budget_maximum_usd"
  | "payment_type"
  | "locations"
  | "timezones"
  | "timezone_overlap"
  | "contract_type"
  | "role"
  | "saved_job_id"
  | "openings_left"
  | "deadline"
  | "start_date"
  | "job_actions"
  | "op_owners"
  | "sale_owners"
  | "interested"
  | "invited_by_client"
  | "invited_by_matcher"
  | "job_type"
  | "offer_accepted_at"
  | "offer_created_at"
  | "offer_updated_at"
  | "bid_created_at"
  | "status_for_freelancer"
> & {
  bid_id?: number;
  employer: Omit<JobEmployer, "login_as_user_id">;
};

export type SimilarJob = Pick<
  JobListingJob,
  | "id"
  | "created"
  | "budget_maximum_usd"
  | "budget_minimum_usd"
  | "employer"
  | "expected_hours_per_week"
  | "job_type"
  | "locations"
  | "payment_type"
  | "role"
  | "saved_job_id"
  | "timezone_overlap"
  | "timezones"
  | "title"
  | "contract_type"
  | "start_date"
  | "deadline"
  | "invited_by_client"
  | "invited_by_matcher"
>;

type DefaultMainJobListingJob = Pick<
  Job,
  | "id"
  | "job_skills"
  | "job_type"
  | "employer"
  | "title"
  | "created"
  | "role"
  | "payment_type"
  | "budget_minimum_usd"
  | "budget_maximum_usd"
  | "locations"
  | "timezones"
  | "timezone_overlap"
  | "expected_hours_per_week"
  | "start_date"
  | "deadline"
  | "contract_type"
  | "openings_left"
  | "has_freelancer_bid"
  | "invited_by_matcher"
  | "invited_by_client"
  | "interested"
  | "saved_job_id"
  | "is_open"
  | "status_for_freelancer"
  | "creator_user_id"
  | "offer_id"
  | "offer_accepted_at"
  | "offer_created_at"
  | "offer_updated_at"
  | "bid_created_at"
> & { bid_id?: number };

export type FreelancerMainJobListingJob = DefaultMainJobListingJob &
  Pick<Job, "main_skills" | "has_freelancer_bid">;

export type NodeStaffMainJobListingJob = DefaultMainJobListingJob &
  Pick<Job, "creator" | "job_actions" | "hired_bids_count"> & {
    op_owners: JobOwnersUserData[];
    sale_owners: JobOwnersUserData[];
  };

export type MainJobListingJob =
  | FreelancerMainJobListingJob
  | DefaultMainJobListingJob
  | NodeStaffMainJobListingJob;

export type ExpandedJobDetails = Pick<
  Job,
  | "contract_type"
  | "deadline"
  | "start_date"
  | "strong_location_requirement_met"
  | "strong_timezone_requirement_met"
> & {
  token_reward: string;
  introduction?: string;
  description?: string;
};

type BidBase = {
  id: number;
  created: IsoDateTime;
  attachments: BidAttachment[];
  payment_amount: string;
  cover_letter: string | null;
  bt_stake: number;
  accepted_at: string | null;
  closed_at: string | null;
  available_start: string | null;
  freelancer_availability: EnumType<typeof ENUMS.FreelancerAvailability> | null;
  freelancer_availability_specific_date: string | null;
  payment_type: EnumType<typeof ENUMS.JobPaymentType>;
  application_questions: Array<ApplicationQuestion>;
  application_answers: Array<ApplicationAnswer>;
  calendar_url: string | null;
  visits_count: number;
  not_hiring_feedback: Array<string>;
  status: EnumType<typeof ENUMS.BidStatus>;
  location_requirement_met?: boolean;
  timezone_requirement_met?: boolean;
  is_skipped_approval_bid?: boolean;
  legally_authorized_to_work?: boolean;
  visa_sponsorship_may_be_required?: boolean;
};

export type FreelancerBidParams = {
  job: number;
  attachments_ids?: number[];
  payment_amount?: string | null;
  bt_stake?: number;
  freelancer_availability?: EnumType<
    typeof ENUMS.FreelancerAvailability
  > | null;
  freelancer_availability_specific_date?: string | null;
  payment_type?: EnumType<typeof ENUMS.JobPaymentType>;
  new_application_answers: Array<
    Partial<ApplicationQuestion> & {
      answer?: string;
    }
  >;
  calendar_url: string | null;
  resume?: number | null;
  store_resume_for_future_bids?: boolean;
  location_requirement_met?: boolean | null;
  timezone_requirement_met?: boolean | null;
  legally_authorized_to_work?: boolean;
  visa_sponsorship_may_be_required?: boolean;
};

export type PublicFreelancerBidParams = WithRecaptchaCode<
  FreelancerBidParams & {
    first_name: string;
    email: string;
    last_name: string;
    linkedin_url: string;
    website_url: string;
    query_params: Record<string, string>;
  }
>;

export type FreelancerBidCurrentOffer = Pick<
  FreelancerOffer,
  "id" | "payment_amount" | "status" | "updated_at"
>;

export type FreelancerBidListItemJob = Pick<
  Job,
  | "id"
  | "budget_maximum_usd"
  | "budget_minimum_usd"
  | "creator_user_id"
  | "employer"
  | "expected_hours_per_week"
  | "is_open"
  | "job_type"
  | "locations"
  | "openings_left"
  | "payment_type"
  | "role"
  | "timezone_overlap"
  | "timezones"
  | "title"
> & { wise_fee: string | null };

export type FreelancerBid = BidBase & {
  freelancer: Pick<
    Freelancer["user"],
    | "id"
    | "first_name"
    | "last_name"
    | "public_name"
    | "profile_url"
    | "has_avatar_set"
    | "avatar"
    | "avatar_thumbnail"
  >;
  resume: ResumeAttachment | null;
  job: Job;
  is_hiring_paused: boolean;
  can_edit: boolean;
  can_cancel: boolean;
  current_offer?: FreelancerBidCurrentOffer;
  status_for_freelancer: EnumType<typeof ENUMS.JobFreelancerStatus>;
  current_offer_created_at: IsoDateTime | null;
  current_offer_accepted_at: IsoDateTime | null;
  current_offer_updated_at: IsoDateTime | null;
  can_be_reported: boolean;
};

export type FreelancerBidListItem = Omit<FreelancerBid, "job"> & {
  job: FreelancerBidListItemJob;
};

export type BidSkill = {
  id: number;
  is_superpower: boolean;
  order: number;
  skill: SkillForJob;
};

export type TalentInviteSuggestion = Pick<
  FreelancerForNodeStaff,
  | "availability_for_work"
  | "availability_for_work_options"
  | "external_profiles"
  | "id"
  | "user"
  | "role"
  | "location"
  | "location_mismatch"
  | "average_internal_review_rating"
  | "internal_review_ratings_count"
> & {
  full_link: string;
  link: string;
  matching_skills: BidSkill[];
  invite_status: EnumType<typeof ENUMS.TalentInviteStatus> | null;
  personal_score_percent: number;
  ada_result?: ResumeSmartMatching;
  ecp_id?: number | null;
};

export type JobInterviewScorecard = {
  attribute_key: string;
  feedback: string;
  id: number;
  score: number;
};
export type JobInterviewTranscript = {
  name: string;
  text: string;
};

export const INTERVIEW_RATING_STATUS = {
  FAIL: "fail",
  MIXED: "mixed",
  PASS: "pass",
} as const;

export type BidInterviewRating = {
  recommended_rating: string;
  scorecard_status: ObjectValues<typeof INTERVIEW_RATING_STATUS>;
};

export type BidInterview = {
  id: number | null;
  is_completed: boolean;
  interview_id: number | null;
  interview_request_sent_at: IsoDateOrIsoDateTime | null;
  overall_feedback: string | null;
  overall_score: string | null;
  scorecard_completed_at: IsoDateOrIsoDateTime | null;
  status: EnumType<typeof ENUMS.JobInterviewStatus>;
  job_interview_scorecards: JobInterviewScorecard[];
  transcript: string | null;
  rating: BidInterviewRating | null;
};

export type ResumeSmartMatchingStrength = {
  text: string;
  confidence: number;
};

export type ResumeSmartMatchingGap = { text: string; confidence: number };

export type ResumeSmartMatching = {
  match_score: number;
  strengths: ResumeSmartMatchingStrength[];
  gaps: ResumeSmartMatchingGap[];
};

export type EmployerBid = BidBase & {
  freelancer: Pick<
    FreelancerForNodeStaff,
    | "approved"
    | "external_profiles"
    | "years_experience"
    | "id"
    | "user"
    | "role"
    | "location"
    | "location_mismatch"
    | "average_internal_review_rating"
    | "internal_review_ratings_count"
  > & {
    address: Address;
    full_link: string;
    link: string;
    average_work_quality: number | null;
    average_responsiveness: number | null;
    review_count: number | null;
    skills: BidSkill[];
    portfolio_items: WorkSampleItem[];
    created_via_public_application_flow?: boolean | null;
    has_phone_number?: boolean;
    has_valid_phone_number?: boolean;
    employment_contact_consented?: boolean;
  };
  can_accept: boolean;
  can_unreject: boolean;
  can_unreject_explanation: string | null;
  can_change_status: boolean;
  compliance_fee_percent: string | null;
  w2_markup_percent: string | null;
  background_fee_amount: string | null;
  can_change_background_fee: boolean;
  braintrust_pays_talent_off_platform_enabled: boolean;
  can_make_offer: boolean;
  can_make_offer_explanation: string | null;
  can_send_interview_request: boolean;
  can_send_interview_request_explanation: string | null;
  pending_offers_exist: boolean;
  job_id: number;
  role: Job["role"];
  is_hidden: boolean;
  is_invited?: boolean;
  proposal_score_percent: number;
  employer_reaction: EnumType<typeof ENUMS.BidReactionType> | null;
  recommended_by_matcher: boolean;
  status: EnumType<typeof ENUMS.BidStatus>;
  application_questions: ApplicationQuestion[];
  application_answers: ApplicationAnswer[];
  calendar_link_filled_score: number;
  notes_count: number;
  resume: ResumeAttachment | null;
  location_requirement_met: boolean | null;
  timezone_requirement_met: boolean | null;
  current_offer: number | null;
  interview: BidInterview | null;
  external_status?: string | null;
  resume_smart_matching?: ResumeSmartMatching;
  freelancer_idv_status?: EnumType<
    typeof ENUMS.IdentityVerificationStatuses
  > | null;
  non_supported_location: boolean;
};

export type EmployerBidListBid = Omit<EmployerBid, "freelancer"> & {
  freelancer: Omit<EmployerBid["freelancer"], "portfolio_items">;
};

export type Bid = FreelancerBid | EmployerBid;

export type FreelancerOfferJob = Pick<
  Job,
  | "id"
  | "employer"
  | "introduction"
  | "job_type"
  | "title"
  | "expected_hours_per_week"
  | "locations"
  | "timezones"
  | "timezone_overlap"
> & { creator: JobCreator };

export type OfferShippingAddress = {
  id: number;
  addressee_first_name: string;
  addressee_last_name: string;
  address1: string;
  address2?: string;
  zipcode: string;
  city: string;
  country: string;
  country_name: string;
  state: string | null;
  test: string;
};

export type OfferContractTerms = ContractTerms & {
  contract_terms_type: EnumType<typeof ENUMS.ContractTermsType>;
};

export type OfferCommon = {
  id: number;
  anticipated_weekly_hours: number | null;
  job_title: string;
  note_to_talent: string | null;
  talent_has_own_equipment: boolean;
  time_of_expiry: string;
  payment_amount: string;
  payment_type: EnumType<typeof ENUMS.JobPaymentType>;
  project_duration: EnumType<typeof ENUMS.JobContractType>;
  proposed_end_date: string | null;
  proposed_start_date: string | null;
  require_paperwork: boolean;
  expired_at: IsoDateOrIsoDateTime | null;
  cancelled_at: IsoDateOrIsoDateTime | null;
  updated_fields: string[];
  shipping_address: OfferShippingAddress | null;
  created: IsoDateOrIsoDateTime;
  updated_at: IsoDateOrIsoDateTime | null;
  hours_limit_type: EnumType<typeof ENUMS.HoursLimitType>;
  availability_details?: string;
  pre_approve_extra_hours: boolean;
  additional_contract_terms: string;
  contract_terms: OfferContractTerms | null;
  pdf_file: OfferPdfFile | null;
};

type FreelancerOfferCreator = {
  id: number;
  public_name: string;
  title: string;
  gravatar: string;
  has_avatar_set: boolean;
  avatar: string;
  avatar_thumbnail: string;
};

export type TalentOfferStatus = EnumType<
  Omit<
    typeof ENUMS.OfferStatus,
    | "WAITING_FOR_DEPOSIT"
    | "WAITING_FOR_DEPOSIT_ACH_CONFIRMATION"
    | "DEPOSIT_PAYMENT_FAILED"
    | "DEPOSIT_PAYMENT_IN_PROGRESS"
  >
>;

export type OfferPdfFile = {
  file_download_url: string;
  file: string;
};

export type FreelancerOffer = OfferCommon & {
  job: FreelancerOfferJob;
  checksum: string;
  creator: FreelancerOfferCreator | null;
  status: TalentOfferStatus;
  role: Role;
  status_for_freelancer: EnumType<typeof ENUMS.JobFreelancerStatus>;
  accepted_at: IsoDateOrIsoDateTime | null;
  requires_extension_acceptance?: boolean;
  idv_acceptance_bypassed?: boolean;
};

export type EmployerOffer = OfferCommon & {
  bid: EmployerOfferBid;
  deposit_payment_method: PaymentMethod;
  reject_reason: string | null;
  batch_invoice_emails: string | null;
  status: EnumType<typeof ENUMS.OfferStatus>;
  is_active_status: EnumType<typeof ENUMS.MyTalentOfferStatus>;
};

export type BidAttachment = {
  id: number;
  attachment: Attachment;
  link: string;
  safe_file_name: string;
};

export type ResumeAttachment = {
  id: number;
  attachment: Pick<
    Attachment,
    "id" | "name" | "file" | "extension" | "mime" | "file_download_url"
  >;
  link: string;
  safe_file_name: string;
};

export type ApplicationQuestion = {
  id: number;
  question: string;
  is_required: boolean;
  is_active: boolean;
  original_question: number;
};

export type ApplicationAnswer = {
  id: number;
  application_question: number;
  answer: string;
};

type Matcher = {
  avatar: string;
  avatar_thumbnail: string;
  first_name: string;
  freelancer_approved: boolean;
  gravatar: string;
  has_avatar_set: boolean;
  id: number;
  last_name: string;
  profile_url: string;
  public_name: string;
  title: string;
};

export type InvitationHistoryItem = {
  id: number;
  freelancer: {
    company_name: string | null;
    id: number;
    user: Pick<
      User,
      "first_name" | "has_address_set" | "id" | "last_name" | "public_name"
    >;
  };
  job: Pick<Job, "id" | "title"> & { employer_name: string };
  matcher: Matcher;
};

export type JobSubType = EnumType<typeof ENUMS.JobSubType>;

export type JobDraft = {
  id: number;
  title: string;
  job_type: EnumType<typeof ENUMS.JobType>;
  openings_number: number;
  public_after_verification?: boolean;
  role: number;
  other_role_name?: string;
  ats_imported: boolean;
  external_id?: string | null;
  creator: JobCreator;

  contract_type?: EnumType<typeof ENUMS.JobContractType>;
  start_date?: string | null;
  deadline?: string | null;
  expected_hours_per_week?: number;

  auto_invite_talent: boolean;

  locations?: JobLocation[];
  locations_strongly_required: boolean | null;

  timezones?: JobDraftTimezone[];
  timezone_overlap: EnumType<typeof ENUMS.JobTimezoneOverlapType> | null;

  top_skills: number[];
  new_skills: number[];
  level?: EnumType<typeof ENUMS.SkillLevel> | null;
  experience_level: EnumType<typeof ENUMS.JobExperienceLevel> | null;
  original_job?: number;

  payment_type: EnumType<typeof ENUMS.JobPaymentType>;

  budget_minimum_usd: string;
  budget_maximum_usd: string;
  min_annual_rate?: string;
  max_annual_rate?: string;
  fixed_rate?: string;

  min_rate?: string;
  max_rate?: string;

  hourly_rate?: typeof RANGE_VALUE;

  requirements?: string;
  description?: string;
  introduction?: string;

  new_application_questions: {
    id: number;
    is_required: boolean;
    original_question: number | null;
    is_active: boolean;
    order?: number;
    can_be_changed?: boolean;
    question: string;
  }[];

  sale_owners?: number[];
  op_owners?: number[];
  hiring_manager?: number;

  is_private: boolean;
  is_resume_required: boolean;

  job_subscribers: JobSubscriberData[];
  job_subscriber_invitations: JobSubscriberInvitationData[];

  job_fill_support_type?: EnumType<typeof ENUMS.JobFillSupportType>;
  employer: number;

  flow_entry?: JobFlowEntryType;

  is_ai_interview_enabled?: boolean;
  ai_interview_max_duration_in_minutes?: number;
  ai_interview_maximum_attempts?: number | null;

  interview_criteria?: InterviewGradingCriteria[];
  interview_questions?: InterviewQuestion[];
  sub_type?: JobSubType;
  talent_invoice_editing_disabled?: boolean;
  skip_approval_to_job?: boolean;
  employer_skip_approval_to_job?: boolean;
  automatic_linkedin_posting_enabled?: boolean | null;
  automatic_vonq_posting_enabled?: boolean | null;
  payment_conditions?:
    "client_pays_directly" | "braintrust_pays_off_platform" | null;
};

export type JobFormInterviewGradingCriteria = InterviewGradingCriteria & {
  isNew?: boolean;
};

export type JobFormValues = Omit<
  CreateJobData,
  | keyof JobRate
  | "timezones"
  | "locations"
  | "job_draft_id"
  | "dry_run"
  | "public_after_verification"
  | "interview_criteria"
> &
  Pick<JobDraft, "id" | "ats_imported"> &
  JobFormRateValues & {
    timezones: TimezoneOption[];
    locations: JobLocation[];
    jobOwnerId?: number;
    isDeletingJobDraft?: boolean;
    employer?: number;
    intro_video?: IntroVideoAttachment;
    interview_criteria?: JobFormInterviewGradingCriteria[];
    payment_conditions?:
      "client_pays_directly" | "braintrust_pays_off_platform" | null;
    employer_skip_approval_to_job?: boolean;
  };

export type JobSubscriberData = { team_member_id: number };

export type JobSubscriber = {
  id: number;
} & JobSubscriberData;

export type JobSubscriberInvitationData = { email: string };

export type JobSubscriberInvitation = {
  id: number;
} & JobSubscriberInvitationData;

export type UpdateJobSubscribersParams = {
  jobId: number;
  jobSubscribers: JobSubscriberData[];
};

export type UpdateJobSubscriberInvitationsParams = {
  jobId: number;
  jobSubscriberInvitations: JobSubscriberInvitationData[];
};

// required fields based on backend response for publishing job
export type ATSJob = {
  remote_id: string;
  external_id: string;
  external_url: string;
  already_imported: boolean;
} & Pick<JobDraft, "title" | "description"> &
  Nullable<
    Pick<
      JobDraft,
      "budget_maximum_usd" | "budget_minimum_usd" | "payment_type" | "locations"
    >
  >;

export type EmployerJobMinimal = Pick<Job, "id" | "title">;

export type GetJobDraftResponse = {
  id: number;
  ats_imported: boolean;
  employer: number;
  creator: JobCreator;
  data: Omit<
    JobDraft,
    | "job_subscribers"
    | "job_subscriber_invitations"
    | "ats_imported"
    | "external_id"
    | "employer_skip_approval_to_job"
  >;
  external_id?: string;
  public_after_verification: boolean;
  pending_approval: boolean;
  job_subscribers: JobSubscriber[];
  job_subscriber_invitations: JobSubscriberInvitation[];
  employer_skip_approval_to_job?: boolean;
};

export type TimezonePayload = {
  timezone: string;
  timezone_id?: string;
};

export type PreparedJobDraftData = Pick<
  CreateJobData,
  | "role"
  | "contract_type"
  | "expected_hours_per_week"
  | "top_skills"
  | "new_skills"
  | "experience_level"
  | "payment_type"
  | "budget_maximum_usd"
  | "budget_minimum_usd"
  | "is_ai_interview_enabled"
  | "ai_interview_max_duration_in_minutes"
  | "ai_interview_maximum_attempts"
  | "interview_criteria"
  | "interview_questions"
  | "auto_invite_talent"
  | "is_private"
  | "locations_strongly_required"
  | "is_resume_required"
  | "public_after_verification"
  | "job_type"
  | "timezone_overlap"
  | "level"
  | "deadline"
  | "start_date"
  | "openings_number"
  | "external_id"
  | "original_job"
  | "op_owners"
  | "sale_owners"
  | "introduction"
  | "description"
  | "requirements"
  | "title"
  | "role"
  | "job_subscribers"
  | "job_subscriber_invitations"
  | "new_application_questions"
  | "hiring_manager"
  | "op_owners"
  | "sale_owners"
  | "job_fill_support_type"
  | "other_role_name"
  | "sub_type"
  | "talent_invoice_editing_disabled"
  | "automatic_linkedin_posting_enabled"
  | "automatic_vonq_posting_enabled"
> & {
  // make sure below fields are unset when preparing the job payload data
  id: number; // job draft id
  jobOwnerId?: undefined;
  isDeletingJobDraft?: undefined;
  locations: JobLocation[];
  timezones: TimezonePayload[];
  employer?: number;
  extension?: UpdateJobDataExtension;
  payment_conditions?:
    "client_pays_directly" | "braintrust_pays_off_platform" | null;
};

export type JobCreator = Pick<
  User,
  | "avatar"
  | "avatar_thumbnail"
  | "created"
  | "first_name"
  | "first_name_possessive"
  | "gravatar"
  | "has_avatar_set"
  | "id"
  | "introduction_headline"
  | "last_name"
  | "location"
  | "profile_url"
  | "public_name"
  | "timezone_abbreviation"
  | "timezone_offset"
  | "title"
  | "can_be_messaged"
> & {
  account_type: AccountType | null; // null for removed user
};

export type CreateJobDraftPayload = {
  data: Omit<
    PreparedJobDraftData,
    "job_subscribers" | "job_subscriber_invitations" | "id"
  >;
  job_subscribers: JobSubscriberData[];
  job_subscriber_invitations: JobSubscriberInvitationData[];
};

export type UpdateJobDraftPayload = {
  data: Omit<
    PreparedJobDraftData,
    "job_subscribers" | "job_subscriber_invitations" | "id"
  > & { id: number };
} & Pick<
  CreateJobDraftPayload,
  "job_subscriber_invitations" | "job_subscribers"
>;

export type GetJobDraftsResponse = PaginatedResult<GetJobDraftResponse>;

export type TalentInviteSuggestions = PaginatedResult<TalentInviteSuggestion>;

export type InviteTalentProps = {
  jobId: number;
  freelancersIds: number[];
  skip_already_invited?: boolean;
};

export type SendJobInvitationResponse = {
  success: boolean;
  skipped_freelancer_ids?: number[];
};

export type RemoveInviteProps = {
  jobId: number;
  freelancerId: number;
};

export type GetSimilarJobsProps = {
  jobId: number;
  pageSize?: number;
};

export type GetSimilarJobsResponse = PaginatedResult<SimilarJob>;

export type RelevantJob = SimilarJob;

export type GetRelevantJobsResponse = PaginatedResult<RelevantJob>;

export type GetRelevantJobsProps = {
  page_size?: number;
  role?: number;
  // comma separated list of job ids
  jobs_ids?: number[];
  ordering?: "best-match" | "newest" | "highest-paid" | "nearest";
};

export type NewApplicationQuestion = {
  id?: number;
  is_required?: boolean;
  original_question?: number | null;
  is_active?: boolean;
  order?: number;
  question: string;
  can_be_changed?: boolean;
};

export type SaveOptionalFeesProps = {
  bidId: number;
  fees: {
    background_fee_amount: string | null;
    compliance_fee_percent: string | null;
    braintrust_pays_talent_off_platform_enabled: boolean | null;
    w2_markup_percent: string | null;
  };
};

export type GetOwnJobsParams = {
  status: JobStatus;
  limit?: number;
};

export type GetJobParams = {
  id: number;
  // referrer enables UNauthenticated user to see private job he entered ...
  // ... through a referral link
  referrer?: string;
};

export type GetExpandedJobDetailsResponse =
  ExpandedJobDetails | ErrorWithDetail;

export type GetExpandedJobDetailsQueryArgs = number;

export type GetSavedJobsParams = {
  page: number;
};

export type GetJobMatchingStatusResponse = {
  ada_matching_status: EnumType<typeof ENUMS.ADAMatchingStatus>;
  analyzing_profiles: number;
};

export type GetJobMatchingStatusArgs = {
  id: number;
};

export type ATSJobToDraftPayload = {
  external_id: string;
  provided_by: number;
  data: Omit<ATSJob, "external_id" | "already_imported" | "remote_id">;
};

export type MoveATSJobsToDraftActionArgs = {
  batch: ATSJobToDraftPayload[];
};

export type AiInterviewCriteria = {
  grade?: number;
  title: string;
  description: string;
};

export type InterviewGradingCriteria = {
  id: number;
  name: string;
  normalized_name: string;
  definition: string;
  poor: string;
  fair: string;
  good: string;
  very_good: string;
  excellent: string;
  order: number;
};

export type InterviewQuestion = {
  id: number;
  question: string;
  order: number;
  follow_up_strategy: EnumType<
    typeof ENUMS.JobInterviewQuestionsFollowUpStrategy
  >;
};

export type JobRate = {
  payment_type: EnumType<typeof ENUMS.JobPaymentType>;
  budget_minimum_usd: string;
  budget_maximum_usd: string;
};

export type JobFormRateValues = {
  payment_type?: EnumType<typeof ENUMS.JobPaymentType>;
  hourly_rate?: string | "range";
  min_rate?: string;
  max_rate?: string;
  fixed_rate?: string;
  min_annual_rate?: string;
  max_annual_rate?: string;
  min_monthly_rate?: string;
  max_monthly_rate?: string;
  custom_min_rate?: string;
  custom_max_rate?: string;
  talent_invoice_editing_disabled?: boolean;
};

export type CompleteJobParams = {
  jobId: number;
  data: {
    client_feedback_was_job_filled: boolean | null;
  };
};

export type CompleteJobResponse = { success: boolean; hired: boolean };

export type CreateJobData = Pick<
  ManagedJob,
  | "title"
  | "contract_type"
  | "budget_maximum_usd"
  | "budget_minimum_usd"
  | "payment_type"
  | "job_type"
  | "top_skills"
  | "flow_entry"
  | "other_role_name"
> &
  Partial<
    Pick<
      ManagedJob,
      | "disable_freelancer_notifications"
      | "introduction"
      | "description"
      | "requirements"
      | "timezone_overlap"
      | "locations_strongly_required"
      | "is_private"
      | "original_job"
      | "experience_level"
      | "is_resume_required"
      | "level"
      | "auto_invite_talent"
      | "is_ai_interview_enabled"
      | "ai_interview_max_duration_in_minutes"
      | "ai_interview_maximum_attempts"
      | "interview_criteria"
      | "interview_questions"
      | "openings_number"
      // from node staff
      | "hiring_manager"
      | "op_owners"
      | "sale_owners"
      | "job_fill_support_type"
      | "sub_type"
      | "talent_invoice_editing_disabled"
      | "automatic_linkedin_posting_enabled"
      | "automatic_vonq_posting_enabled"
      | "skip_approval_to_job"
    >
  > & {
    role: number;
    start_date?: string;
    deadline?: string;
    expected_hours_per_week: number;
    timezones?: TimezonePayload[];
    locations?: JobLocation[];
    new_application_questions?: NewApplicationQuestion[];

    job_subscribers?: JobSubscriberData[];
    job_subscriber_invitations?: JobSubscriberInvitationData[];
    new_skills: number[];
    job_draft_id: number;
    external_id?: string;

    dry_run?: boolean;
    public_after_verification?: boolean;
    public_after_approval?: boolean;
    client_pays_talent_directly?: boolean;
    braintrust_pays_talent_off_platform?: boolean;
  };

export type CreateJobResponse = {
  id: number;
  non_supported_location_warnings?: string[];
};

export type UpdateJobData = Pick<
  CreateJobData,
  | "title"
  | "contract_type"
  | "deadline"
  | "start_date"
  | "expected_hours_per_week"
  | "openings_number"
  | "role"
  | "introduction"
  | "description"
  | "requirements"
  | "budget_maximum_usd"
  | "budget_minimum_usd"
  | "level"
  | "payment_type"
  | "new_application_questions"
  | "auto_invite_talent"
  | "locations"
  | "locations_strongly_required"
  | "timezones"
  | "timezone_overlap"
  | "new_skills"
  | "top_skills"
  | "is_private"
  | "experience_level"
  | "is_resume_required"
  | "job_subscribers"
  | "job_subscriber_invitations"
  | "job_fill_support_type"
  | "other_role_name"
  | "automatic_linkedin_posting_enabled"
  | "automatic_vonq_posting_enabled"
> & {
  extension?: UpdateJobDataExtension;
  job_type?: CreateJobData["job_type"];
  sub_type?: CreateJobData["sub_type"] | null;
  talent_invoice_editing_disabled?:
    CreateJobData["talent_invoice_editing_disabled"] | null;
  client_pays_talent_directly?: boolean;
  braintrust_pays_talent_off_platform?: boolean;
  skip_approval_to_job?: CreateJobData["skip_approval_to_job"];
};

export type UpdateJobDataExtension = {
  intro_video?: number | null;
};

export type UpdateJobParams = {
  jobId: number;
  data: UpdateJobData;
};

export type PatchJobParams = { jobId: number; data: Partial<UpdateJobData> };

export type GetTotalJobBidsResponse = { bids_count: number };
export type GetTotalJobBidsParams = { jobId: number };

export type UpdateJobFeedbackData = {
  job_not_filled_reason?: EnumType<typeof ENUMS.JobNotFilledReasonType>;
  job_filled_reason?: EnumType<typeof ENUMS.JobFilledReasonType>;
  job_filled_using_platform?: EnumType<
    typeof ENUMS.JobFilledUsingPlatformType
  > | null;
  reason_other?: string | null;
  quality_of_applications_received_rate: string;
};

export type UpdateJobFeedbackParams = {
  jobId: number;
  data: UpdateJobFeedbackData;
};

export type GetJobDraftsParams = {
  limit?: number;
  pending_approval?: boolean;
};

export type GetJobsParams = Partial<JobFilters> & {
  page?: number;
  sub_type?: JobSubType;
  created_after?: string;
};

export type GetJobsResponse = PaginatedResult<JobListingJob> & {
  total_openings_count: number;
};

export type JobExternalReposting = {
  automatic_linkedin_posting_enabled: boolean | null;
  is_eligible_for_linkedin_posting: boolean;
  linkedin_promotion_expires_at: string;
  linkedin_cannot_be_posted_reason: string | null;
  automatic_vonq_posting_enabled: boolean | null;
  is_eligible_for_vonq_posting: boolean;
  vonq_promotion_expires_at: string;
  vonq_cannot_be_posted_reason: string | null;
};

export type GenerateSingleGradingCriteriaParams = {
  jobId: number;
  definition: string;
  questions: string[];
};

export type GenerateSingleGradingCriteriaDetailsResponse = Omit<
  InterviewGradingCriteria,
  "id"
>;

export type GenerateGradingCriteriaResponse = Array<
  Omit<InterviewGradingCriteria, "id">
>;

export type GenerateGradingCriteriaParams = {
  jobId: number;
  questions: string[];
};

export type ContractTerms = {
  content: string;
  created: string;
  description: string;
  id: number;
  version: number;
};

export type BoilerplateTerms = {
  id: number;
  name: string;
  contract_terms_type: EnumType<typeof ENUMS.ContractTermsType>;
  contract_terms: ContractTerms;
};

export type GetBoilerplateTermsResponse = BoilerplateTerms[];

export type PreviewBulkOffersPayload = {
  jobId: number;
  emails: string[];
};

export type PreviewBulkOfferItem = {
  email: string;
  is_matched: boolean;
  can_make_offer: boolean;
  can_make_offer_explanation: string | null;
  bid_id: number | null;
  freelancer_id: number | null;
  freelancer_first_name: string | null;
  freelancer_last_name: string | null;
  status: EnumType<typeof ENUMS.BidStatus>;
  payment_amount: string | null;
  payment_type: EnumType<typeof ENUMS.JobPaymentType>;
  match_score: string | null;
  ai_score: string | null;
  freelancer_idv_status: EnumType<
    typeof ENUMS.IdentityVerificationStatuses
  > | null;
};

export type PreviewBulkOffersResponse = PreviewBulkOfferItem[];
